import React, { FC, KeyboardEvent, useEffect, useState } from "react";

import { makeStyles, tokens } from "@fluentui/react-components";
import {
  SendFilled,
  SendRegular,
} from "@fluentui/react-icons";

import { translate } from "@/utils/translations";
import { DocumentStatus } from "@/pages/Thread/types";

import { ContextIndicator } from "./ContextIndicator";

interface ChatInputProps {
  contextText?: string;
  disabled?: boolean;
  disableSubmit?: boolean;
  documentStatus?: DocumentStatus;
  placeholder?: string;
  showContextIndicator?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
}

export const ChatInput: FC<ChatInputProps> = ({
  contextText = "Using full document as context",
  disabled = false,
  disableSubmit = false,
  documentStatus,
  onChange,
  onSubmit,
  placeholder = "Ask anything about the document",
  showContextIndicator = true,
  value = "",
}) => {
  const styles = useStyles();
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Sync internal state with external value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSubmit = () => {
    if (inputValue.trim() && onSubmit && !disableSubmit) {
      onSubmit(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disableSubmit) {
        handleSubmit();
      }
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div
          className={`${styles.inputContainer} ${
            isFocused
              ? styles.inputContainerFocused
              : styles.inputContainerNotFocused
          }`}
        >
          <div className={styles.textareaWrapper}>
            <textarea
              className={styles.input}
              disabled={disabled}
              onBlur={() => setIsFocused(false)}
              onChange={handleInputChange}
              onFocus={() => setIsFocused(true)}
              onKeyDown={handleKeyDown}
              placeholder={translate(placeholder)}
              rows={1}
              value={inputValue}
            />
            <span aria-hidden="true" className={styles.invisibleSpan}>
              {inputValue + "\u200b"}
            </span>
          </div>

          <div className={styles.bottomRow}>
            {/* Context indicator */}
            {showContextIndicator && (
              <ContextIndicator
                contextText={contextText}
                documentStatus={documentStatus}
              />
            )}

            {/* Send button */}
            <button
              aria-label="Send message"
              className={`${styles.sendButton} ${
                inputValue.trim() && !disableSubmit
                  ? styles.sendButtonFocused
                  : ""
              }`}
              disabled={disabled || !inputValue.trim() || disableSubmit}
              onClick={handleSubmit}
              title="Send message"
              type="button"
            >
              {disabled || !inputValue.trim() || disableSubmit ? (
                <SendRegular className={styles.sendIcon} />
              ) : (
                <SendFilled className={styles.sendIcon} />
              )}
            </button>
          </div>
        </div>
      </div>
	<span className={styles.disclaimer}>
	  {translate('Generated content may be incorrect. Please verify important information.')}
	</span>
    </>
  );
};

const useStyles = makeStyles({
  bottomRow: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
  },

  container: {
    border: "1px solid #D1D5DB",
    borderRadius: "6px",
    overflow: "hidden",
    width: "100%",
  },

  disclaimer: {
    color: tokens.colorNeutralForeground4,
    display: "inline-block",
    fontSize: tokens.fontSizeBase100,
	lineHeight: tokens.lineHeightBase100,
    paddingTop: "4px",
    textAlign: "center",
    width: "100%",
  },
  input: {
    "&::placeholder": {
      color: "#6B7280",
    },
    "&:focus": {
      boxShadow: "none",
      outline: "none",
    },
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    color: "#111827",
    fontSize: "14px",
    gridArea: "1 / 1 / 2 / 2",
    maxHeight: "200px",
    minHeight: "20px",
    outline: "none",
    overflow: "auto",
    padding: "0",
    paddingBottom: "8px",
    resize: "none",
    width: "100%",
    wordBreak: "break-word",
  },
  inputContainer: {
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
    padding: "6px 12px 10px",
    transition: "all 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  },
  inputContainerFocused: {
    boxShadow: "inset rgb(41, 121, 255) 0px -3px 0px 0px",
  },
  inputContainerNotFocused: {
    boxShadow: "inset transparent 0px -3px 0px 0px",
  },
  invisibleSpan: {
    color: "transparent",
    fontSize: "14px",
    gridArea: "1 / 1 / 2 / 2",
    maxHeight: "200px",
    padding: "0",
    paddingTop: "8px",
    visibility: "hidden",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
  },
  sendButton: {
    "&:disabled": {
      backgroundColor: tokens.colorNeutralBackground1,
      cursor: "not-allowed",
    },
    alignItems: "center",
    backgroundColor: "transparent",
    border: "none",
    color: tokens.colorNeutralForeground4,
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    transition: "color 0.2s, background-color 0.2s",
  },
  sendButtonFocused: {
    color: tokens.colorNeutralForeground1,
  },
  sendIcon: {
    height: "20px",
    width: "20px",
  },
  textareaWrapper: {
    display: "grid",
    width: "100%",
  },
});
